import { useMediaQuery, useTheme } from '@material-ui/core';
import queryString from 'query-string';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { PIPELINE_CARD_STAGE_ID } from 'containers/ApplicantsV2/constants';
import { State as ApplicantsV2State } from 'containers/ApplicantsV2/reducer';
import makeSelectApplicantsV2 from 'containers/ApplicantsV2/selectors';
import { setApplicantMessagesAction } from 'containers/ApplicantTableV2/actions';
import {
  makeSelectApplicants,
  makeSelectHasFetchedApplicants,
  makeSelectIsFetchingApplicants,
} from 'containers/ApplicantTableV2/selectors';
import { messages as openingMessages } from 'containers/Openings/components/FilterableOpeningsTable/messages';
import { ApplicantDetailOutcome } from 'hooks/useApplicantDetail';
import { usePostHog } from 'hooks/usePostHog';
import { resolveProfileViewSource } from 'utils/applicantProfileView';
import { addApplicantTransitionedListener } from 'utils/appEvents';
import { history } from 'utils/history';
import {
  getApplicantTableScope,
  removeParamsFromUrl,
  setParamsToUrl,
} from 'utils/urlUtils';

import { isUniversalSearchOpen, loadQueue, LoadQueueInput } from './loadQueue';
import { messages } from './messages';

type EntryPoint = 'resume_link' | 'files_tab';
type ExitMethod = 'button' | 'escape' | 'drawer_close' | 'navigation';
export type NavigationMethod = 'button' | 'arrow_key' | 'auto_advance';
interface QueueSnapshot {
  ids: string[];
  truncated: boolean;
  label: string;
}
interface DetailSettlement {
  applicantId: string;
  outcome: ApplicantDetailOutcome;
}
interface ResumeReviewContextValue {
  isFullPage: boolean;
  enter: (entryPoint: EntryPoint) => void;
  exit: (exitMethod: ExitMethod) => void;
  hasEntered: boolean;
  queue: (QueueSnapshot & { position: number }) | null;
  go: (direction: 'next' | 'previous', method: NavigationMethod) => void;
  isNavigating: boolean;
  navigationMethod: NavigationMethod | null;
  reviewSessionId: string | null;
  detailSettlement: DetailSettlement | null;
  reportDetailSettled: (
    applicantId: string,
    outcome: ApplicantDetailOutcome,
  ) => void;
}

const noop = () => {};
const defaultResumeReview: ResumeReviewContextValue = {
  isFullPage: false,
  enter: noop,
  exit: noop,
  queue: null,
  hasEntered: false,
  go: noop,
  isNavigating: false,
  navigationMethod: null,
  reviewSessionId: null,
  detailSettlement: null,
  reportDetailSettled: noop,
};
const ResumeReviewContext = createContext<ResumeReviewContextValue | null>(
  null,
);

export function ResumeReviewProvider({ children }: PropsWithChildren) {
  const flagOn = Boolean(
    useSelector(makeSelectWhoami()).feature_flags?.['applicant-resume-review'],
  );
  const theme = useTheme();
  const mobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { search } = location;
  const params = queryString.parse(search);
  const applicantId =
    typeof params.applicantId === 'string' ? params.applicantId : '';
  const isFullPage = flagOn && !mobileDevice && params.resumeReview === '1';
  const posthog = usePostHog();
  const intl = useIntl();
  const dispatch = useDispatch();
  const applicants = useSelector(makeSelectApplicants());
  const isFetchingApplicants = useSelector(makeSelectIsFetchingApplicants());
  const hasFetchedApplicants = useSelector(makeSelectHasFetchedApplicants());
  const { job, searchQuery }: ApplicantsV2State = useSelector(
    makeSelectApplicantsV2(),
  );
  const [entry, setEntry] = useState<Omit<LoadQueueInput, 'tableIds'> | null>(
    null,
  );
  const entryRef = useRef<typeof entry>(null);
  const queueAttempted = useRef(false);
  const snapshotRef = useRef<QueueSnapshot | null>(null);
  const [snapshot, setSnapshot] = useState<QueueSnapshot | null>(null);
  const expectedDetailId = useRef<string | null>(null);
  const settledDetailId = useRef<string | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigation, setNavigation] = useState<{
    targetId: string;
    method: NavigationMethod;
  } | null>(null);
  const previousApplicantId = useRef(applicantId);
  if (previousApplicantId.current !== applicantId) {
    previousApplicantId.current = applicantId;
    if (navigation && navigation.targetId !== applicantId) setNavigation(null);
  }
  const reviewSessionId = useRef<string | null>(null);
  if (isFullPage && !reviewSessionId.current)
    reviewSessionId.current = crypto.randomUUID();
  const activeReview = useRef(false);
  const mounted = useRef(false);
  const [detailSettlement, setDetailSettlement] =
    useState<DetailSettlement | null>(null);

  if (
    entry &&
    ((snapshot &&
      (!snapshot.ids.includes(applicantId) ||
        isUniversalSearchOpen(location))) ||
      (!snapshot && entry.currentId !== applicantId) ||
      isUniversalSearchOpen(entry.location) !== isUniversalSearchOpen(location))
  ) {
    entryRef.current = null;
    snapshotRef.current = null;
    queueAttempted.current = false;
    setEntry(null);
    setSnapshot(null);
    setNavigation(null);
  }

  const captureEntry = useCallback(() => {
    if (entryRef.current) return;
    const { jobId, stageId } = getApplicantTableScope({ location });
    const input = {
      location,
      currentId: applicantId,
      searchQuery,
      stageId,
      funnelId: jobId,
    };
    entryRef.current = input;
    setEntry(input);
  }, [location, applicantId, searchQuery]);

  const captureClose = useCallback(
    (exitMethod: ExitMethod) => {
      if (!activeReview.current) return;
      activeReview.current = false;
      posthog.capture('applicants:resume_review_close', {
        exit_method: exitMethod,
        review_session_id: reviewSessionId.current,
      });
    },
    [posthog],
  );

  const endCapture = useCallback(
    (exitMethod: ExitMethod) => {
      captureClose(exitMethod);
      entryRef.current = null;
      snapshotRef.current = null;
      queueAttempted.current = false;
      expectedDetailId.current = null;
      reviewSessionId.current = null;
      setEntry(null);
      setSnapshot(null);
      setNavigation(null);
      setIsNavigating(false);
    },
    [captureClose],
  );

  // URL restoration starts a session without attributing an invented entry point.
  useEffect(() => {
    if (isFullPage) {
      reviewSessionId.current ??= crypto.randomUUID();
      activeReview.current = true;
      captureEntry();
    } else {
      endCapture(params.persistedPopupType ? 'navigation' : 'drawer_close');
    }
  }, [isFullPage, params.persistedPopupType, endCapture, captureEntry]);

  // The close saga clears the URL before GlobalPopup unmounts this provider.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // Strict Mode reconnects effects synchronously without closing the drawer.
      void Promise.resolve().then(() => {
        if (!mounted.current) captureClose('drawer_close');
      });
    };
  }, [captureClose]);

  // The table store settles independently of the drawer's detail request.
  useEffect(() => {
    if (!entry || queueAttempted.current) return;
    const source = resolveProfileViewSource({
      isPagination: false,
      pathname: entry.location.pathname,
      search: entry.location.search,
      accountSlug: entry.location.pathname.split('/')[1],
    });
    const isList =
      !isUniversalSearchOpen(entry.location) &&
      (source === 'pipeline_board' ||
        source === 'applicant_list' ||
        source === 'applicant_search');
    if (isList && (!hasFetchedApplicants || isFetchingApplicants)) return;
    if (source === 'pipeline_board' && job?.id !== entry.funnelId) return;
    const stage = job?.stages.find(stage => stage.id === entry.stageId);
    if (
      source === 'pipeline_board' &&
      entry.stageId !== PIPELINE_CARD_STAGE_ID &&
      !stage
    )
      return;
    let label = intl.formatMessage(messages.universalApplicantView);
    if (
      source === 'pipeline_board' &&
      entry.stageId === PIPELINE_CARD_STAGE_ID
    ) {
      label = intl.formatMessage(openingMessages.pipeline);
    } else if (source === 'pipeline_board' && stage) {
      label = stage.name;
    }
    queueAttempted.current = true;
    const requestEntry = entry;
    void loadQueue({ ...entry, tableIds: Object.keys(applicants) }).then(
      result => {
        if (!mounted.current || entryRef.current !== requestEntry) return;
        const fixedQueue = result ? { ...result, label } : null;
        snapshotRef.current = fixedQueue;
        setSnapshot(fixedQueue);
      },
    );
  }, [
    entry,
    applicants,
    hasFetchedApplicants,
    isFetchingApplicants,
    job,
    intl,
  ]);

  const enter = useCallback(
    (entryPoint: EntryPoint) => {
      if (!flagOn || mobileDevice || activeReview.current) return;
      reviewSessionId.current ??= crypto.randomUUID();
      activeReview.current = true;
      captureEntry();
      setParamsToUrl({ resumeReview: '1' });
      posthog.capture('applicants:resume_review_open', {
        entry_point: entryPoint,
        review_session_id: reviewSessionId.current,
      });
    },
    [flagOn, mobileDevice, posthog, captureEntry],
  );
  const exit = useCallback(
    (exitMethod: ExitMethod) => {
      endCapture(exitMethod);
      removeParamsFromUrl({ resumeReview: '' });
    },
    [endCapture],
  );
  const reportDetailSettled = useCallback(
    (applicantId: string, outcome: ApplicantDetailOutcome) => {
      const currentId = queryString.parse(history.location.search).applicantId;
      if (applicantId !== currentId) return;
      settledDetailId.current = applicantId;
      expectedDetailId.current = null;
      setIsNavigating(false);
      setDetailSettlement({ applicantId, outcome });
    },
    [],
  );
  const go = useCallback(
    (direction: 'next' | 'previous', method: NavigationMethod) => {
      if (!flagOn || mobileDevice || expectedDetailId.current) return;
      const currentId = queryString.parse(history.location.search).applicantId;
      if (
        typeof currentId !== 'string' ||
        settledDetailId.current !== currentId
      )
        return;
      const fixedQueue = snapshotRef.current;
      const position = fixedQueue?.ids.indexOf(currentId) ?? -1;
      if (!fixedQueue || position < 0) return;
      const targetId =
        fixedQueue.ids[position + (direction === 'next' ? 1 : -1)];
      if (!targetId) return;
      expectedDetailId.current = targetId;
      settledDetailId.current = null;
      setIsNavigating(true);
      setNavigation({ targetId, method });
      setParamsToUrl({ applicantId: targetId });
      dispatch(setApplicantMessagesAction(currentId, []));
    },
    [flagOn, mobileDevice, dispatch],
  );
  const position = snapshot?.ids.indexOf(applicantId) ?? -1;
  const currentReviewSessionId = isFullPage ? reviewSessionId.current : null;
  const navigationMethod =
    navigation?.targetId === applicantId ? navigation.method : null;
  const hasEntered = isFullPage && entry !== null;
  const queue = useMemo(
    () =>
      hasEntered && snapshot && position >= 0
        ? { ...snapshot, position }
        : null,
    [hasEntered, snapshot, position],
  );
  useEffect(() => {
    if (!isFullPage || !queue) return;
    return addApplicantTransitionedListener(event => {
      if (event.detail.applicantId === applicantId) go('next', 'auto_advance');
    });
  }, [isFullPage, queue, applicantId, go]);
  const value = useMemo(
    () => ({
      isFullPage,
      enter,
      exit,
      hasEntered,
      queue,
      go,
      isNavigating,
      navigationMethod,
      reviewSessionId: currentReviewSessionId,
      detailSettlement,
      reportDetailSettled,
    }),
    [
      isFullPage,
      enter,
      exit,
      hasEntered,
      queue,
      go,
      isNavigating,
      navigationMethod,
      currentReviewSessionId,
      detailSettlement,
      reportDetailSettled,
    ],
  );

  return (
    <ResumeReviewContext.Provider value={value}>
      {children}
    </ResumeReviewContext.Provider>
  );
}

export function useResumeReview() {
  return useContext(ResumeReviewContext) ?? defaultResumeReview;
}

export function useOptionalResumeReview() {
  return useContext(ResumeReviewContext);
}
